import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  addNote,
  getRecruit,
  getUploadDownloadUrl,
  hoursSince,
  moveRecruit,
  STAGES,
  type Recruit,
  type RecruitActivity,
  type RecruitUpload,
  type Stage,
} from '../api/recruits';

const TABS = ['Overview', 'Documents', 'Contracts', 'Training', 'Licensing', 'Communication', 'Tasks', 'Activity'] as const;
type Tab = typeof TABS[number];

const STUBBED: Record<string, string> = {
  Contracts: 'Sprint 9',
  Training: 'Sprint 9',
  Licensing: 'Sprint 11',
  Communication: 'Sprint 9',
  Tasks: 'Sprint 9',
};

const UPLOAD_LABELS: Record<RecruitUpload['field'], string> = {
  resume: 'Resume',
  license: 'License Cert',
  voidedCheck: 'Voided Check',
};

function filenameFromS3Key(s3Key: string): string {
  const last = s3Key.split('/').pop() || s3Key;
  return last;
}

function humanFileSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString();
}

export default function RecruitingDossier() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [recruit, setRecruit] = useState<Recruit | null>(null);
  const [activity, setActivity] = useState<RecruitActivity[]>([]);
  const [tab, setTab] = useState<Tab>('Overview');
  const [error, setError] = useState<string | null>(null);
  const [noteBody, setNoteBody] = useState('');
  const [moveOpen, setMoveOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [downloadingIdx, setDownloadingIdx] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  async function reload() {
    if (!id) return;
    try {
      const data = await getRecruit(id);
      setRecruit(data.recruit);
      setActivity(data.activity);
      const created = new URLSearchParams(location.search).has('created');
      if (created) setToast(`Recruit created — ${data.recruit.firstName} ${data.recruit.lastName}`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  async function onAddNote() {
    if (!recruit || !noteBody.trim()) return;
    try {
      await addNote(recruit._id, noteBody.trim());
      setNoteBody('');
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to add note');
    }
  }

  async function onDownload(idx: number) {
    if (!recruit) return;
    setDownloadError(null);
    setDownloadingIdx(idx);
    try {
      const { url } = await getUploadDownloadUrl(recruit._id, idx);
      // Open in a new tab — short-lived presigned URL goes straight to R2.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setDownloadError(err.response?.data?.error?.message || (e instanceof Error ? e.message : 'Download failed'));
    } finally {
      setDownloadingIdx(null);
    }
  }

  async function onMove(s: Stage) {
    if (!recruit) return;
    setMoveOpen(false);
    try {
      await moveRecruit(recruit._id, s);
      setError(null);
      reload();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string; code?: string } } } };
      const apiErr = err.response?.data?.error;
      if (apiErr?.code === 'STAGE_TRANSITION_FORBIDDEN') {
        setError('You can only advance stages by one at a time. Ask an admin to jump further.');
      } else {
        setError(apiErr?.message || (e instanceof Error ? e.message : 'Move failed'));
      }
    }
  }

  if (!recruit) {
    return <div className="px-6 py-6 text-dg-mist">{error || 'Loading…'}</div>;
  }

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto">
        {toast && (
          <div className="mb-4 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded text-sm text-emerald-300">
            {toast}
          </div>
        )}
        {error && (
          <div className="mb-4 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-300">
            {error}
          </div>
        )}
        <Link to="/recruiting/pipeline" className="text-dg-mist text-sm hover:text-white">
          ← Back
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4">
          <div>
            <h1 className="h-display text-3xl">{recruit.firstName} {recruit.lastName}</h1>
            <div className="text-sm text-dg-mist mt-1">
              {recruit.state} · {recruit.licensedStatus} · Comp {recruit.compPercent}%
            </div>
            <div className="text-sm text-dg-text-2 mt-1">
              <span className="capitalize">Stage: {recruit.stage}</span>
              {' · '}In stage {Math.round(hoursSince(recruit.stageEnteredAt))}h
            </div>
          </div>
          <div className="flex items-center gap-2 relative">
            <button
              onClick={() => setMoveOpen((v) => !v)}
              className="bg-dg-panel hover:bg-dg-surface border border-dg-border text-sm px-3 py-1.5 rounded-md"
            >
              Move stage ▾
            </button>
            {moveOpen && (
              <div className="absolute top-10 right-0 w-48 bg-dg-panel border border-dg-border rounded-md shadow-lg z-10 py-1">
                {STAGES.map((s) => (
                  <button
                    key={s}
                    onClick={() => onMove(s)}
                    className="block w-full text-left text-sm px-3 py-1.5 hover:bg-white/5 capitalize"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 border-b border-dg-border flex gap-1">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={
                tab === t
                  ? 'px-4 py-2 text-sm border-b-2 border-dg-red text-white'
                  : 'px-4 py-2 text-sm text-dg-mist hover:text-white'
              }
            >
              {t}
              {STUBBED[t] && <span className="ml-1 text-[9px] uppercase tracking-wider text-dg-mist/60">soon</span>}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === 'Overview' && (
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 bg-dg-panel/60 border border-dg-border rounded-lg p-4">
                <h2 className="text-xs uppercase tracking-wider text-dg-mist mb-3">Recruit details</h2>
                <dl className="space-y-1.5 text-sm">
                  <Detail k="Email" v={recruit.email} />
                  <Detail k="Cell" v={recruit.cellPhone} />
                  <Detail k="State" v={recruit.state} />
                  <Detail k="Licensed" v={recruit.licensedStatus} />
                  <Detail k="Lead source" v={recruit.leadSource || '—'} />
                  <Detail k="Comp %" v={`${recruit.compPercent}%`} />
                  <Detail k="Direct upline" v={recruit.directUplineName} />
                  <Detail k="Upline email" v={recruit.directUplineEmail} />
                  <Detail k="Notes" v={recruit.notes || '—'} />
                </dl>
              </div>
              <div className="bg-dg-panel/60 border border-dg-border rounded-lg p-4">
                <h2 className="text-xs uppercase tracking-wider text-dg-mist mb-3">Status & timeline</h2>
                <dl className="space-y-1.5 text-sm">
                  <Detail k="Stage" v={recruit.stage} />
                  <Detail k="In stage" v={`${Math.round(hoursSince(recruit.stageEnteredAt))}h`} />
                  <Detail k="Created" v={fmtDateTime(recruit.createdAt)} />
                  <Detail k="TCPA at" v={fmtDateTime(recruit.tcpaConsentAt)} />
                </dl>
              </div>
              <div className="col-span-3 bg-dg-panel/60 border border-dg-border rounded-lg p-4">
                <h2 className="text-xs uppercase tracking-wider text-dg-mist mb-3">Add a note</h2>
                <textarea
                  className="w-full bg-dg-surface border border-dg-border rounded-md px-3 py-2 text-sm text-white"
                  rows={3}
                  placeholder="Note for the recruit dossier…"
                  value={noteBody}
                  onChange={(e) => setNoteBody(e.target.value)}
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={onAddNote}
                    disabled={!noteBody.trim()}
                    className="bg-dg-red hover:bg-dg-red-hover disabled:opacity-40 text-white text-sm px-4 py-1.5 rounded-md"
                  >
                    Save note
                  </button>
                </div>
              </div>
            </div>
          )}

          {tab === 'Activity' && (
            <div className="space-y-3" data-testid="activity-feed">
              {activity.length === 0 ? (
                <div className="text-dg-mist text-sm">No activity yet.</div>
              ) : (
                activity.map((a) => (
                  <div key={a._id} className="bg-dg-panel/60 border border-dg-border rounded-lg p-3">
                    <div className="flex items-center justify-between text-xs text-dg-mist mb-1">
                      <span className="font-medium text-dg-text-2">{a.actorName || 'system'}</span>
                      <span>{fmtDateTime(a.createdAt)}</span>
                    </div>
                    {a.type === 'created' && <div className="text-sm">Recruit created.</div>}
                    {a.type === 'stage_changed' && (
                      <div className="text-sm">
                        Stage <span className="capitalize">{a.fromStage}</span> →{' '}
                        <span className="capitalize text-white font-medium">{a.toStage}</span>
                      </div>
                    )}
                    {a.type === 'note_added' && (
                      <div className="text-sm border-l-2 border-dg-red pl-3 italic text-dg-text-2">
                        “{a.noteBody}”
                      </div>
                    )}
                    {a.type === 'owner_changed' && (
                      <div className="text-sm">Owner reassigned.</div>
                    )}
                    {a.type === 'field_updated' && (
                      <div className="text-sm">
                        <span className="capitalize">{a.field}</span> changed
                        {' '}from <code className="text-dg-mist">{String(a.oldValue ?? '∅')}</code>
                        {' '}to <code className="text-white">{String(a.newValue ?? '∅')}</code>
                      </div>
                    )}
                    {a.type === 'system' && <div className="text-sm text-dg-mist">{a.noteBody}</div>}
                  </div>
                ))
              )}
            </div>
          )}

          {tab === 'Documents' && (
            <div className="space-y-3" data-testid="documents-tab">
              {downloadError && (
                <div className="px-4 py-2 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-300">
                  {downloadError}
                </div>
              )}
              {!recruit.uploads || recruit.uploads.length === 0 ? (
                <div className="text-dg-mist text-sm bg-dg-panel/40 border border-dg-border rounded-lg p-8 text-center">
                  No documents uploaded yet.
                </div>
              ) : (
                recruit.uploads.map((u, idx) => (
                  <div
                    key={`${u.s3Key}-${idx}`}
                    className="bg-dg-panel/60 border border-dg-border rounded-lg p-3 flex items-center justify-between gap-4"
                    data-testid="document-item"
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-white font-medium">{UPLOAD_LABELS[u.field] || u.field}</div>
                      <div className="text-xs text-dg-mist truncate">{filenameFromS3Key(u.s3Key)}</div>
                      <div className="text-xs text-dg-text-2 mt-0.5">
                        {u.mimeType || 'unknown type'} · {humanFileSize(u.size)} · uploaded {fmtDateTime(u.uploadedAt)}
                      </div>
                    </div>
                    <button
                      onClick={() => onDownload(idx)}
                      disabled={downloadingIdx === idx}
                      className="bg-dg-red hover:bg-dg-red-hover disabled:opacity-40 text-white text-sm px-3 py-1.5 rounded-md shrink-0"
                    >
                      {downloadingIdx === idx ? 'Loading…' : 'Download'}
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {STUBBED[tab] && (
            <div className="text-dg-mist text-sm bg-dg-panel/40 border border-dg-border rounded-lg p-8 text-center">
              <div className="h-display text-2xl mb-2">{tab}</div>
              <div>Coming soon — {STUBBED[tab]}.</div>
            </div>
          )}
        </div>
    </div>
  );
}

function Detail({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="text-dg-mist w-32 shrink-0 capitalize">{k}</dt>
      <dd className="text-white">{v}</dd>
    </div>
  );
}
